import * as THREE from "three"
import {GLTFLoader} from "../vendor/three/examples/jsm/loaders/GLTFLoader.js"
import {fitDistance, slots} from "./glb_preview.js"

const initers = new Map()

export const GLBPreview = {
  mounted() {
    this._init = () => {
      initers.delete(this.el.id)
      if (!this.el.isConnected || this._view) return
      const url = this.el.dataset.src
      if (!url) return
      this._view = createView(this.el, url, {
        interactive: this.el.dataset.interactive === "true",
        spin: Number(this.el.dataset.spin || 0.3),
      })
    }
    if (slots.acquire(this.el.id)) {
      this._init()
    } else {
      initers.set(this.el.id, this._init)
    }
  },

  destroyed() {
    initers.delete(this.el.id)
    // Dispose before releasing the slot so a woken waiter never creates a
    // new WebGL context while the old one is still live.
    this._view?.dispose()
    this._view = null
    const woken = slots.release(this.el.id)
    const init = woken && initers.get(woken)
    if (init) init()
  },
}

function createView(root, url, opts) {
  const canvas = root.querySelector("canvas")
  const fallback = root.querySelector("[data-glb-fallback]")
  if (!canvas) return null

  const renderer = new THREE.WebGLRenderer({canvas, antialias: true, alpha: true})
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
  renderer.outputColorSpace = THREE.SRGBColorSpace

  const scene = new THREE.Scene()
  scene.add(new THREE.HemisphereLight(0xe8f0ff, 0x3d4a3a, 1.05))
  const sun = new THREE.DirectionalLight(0xfff4d6, 1.1)
  sun.position.set(40, 70, 20)
  scene.add(sun)

  const camera = new THREE.PerspectiveCamera(45, 1, 0.01, 100)
  const group = new THREE.Group()
  scene.add(group)

  let fitted = null
  const fitCamera = (size) => {
    const w = Math.max(1, canvas.clientWidth)
    const h = Math.max(1, canvas.clientHeight)
    camera.aspect = w / h
    const dist = fitDistance(size, w / h, camera.fov)
    camera.far = dist * 10
    camera.updateProjectionMatrix()
    camera.position.set(0, size.y * 0.12, dist)
    camera.lookAt(0, 0, 0)
  }

  const orbit = {yaw: 0.6, pitch: -0.15}
  let dragging = false
  let lastX = 0
  let lastY = 0
  let running = true
  let last = performance.now()

  const applyOrbit = () => group.rotation.set(orbit.pitch, orbit.yaw, 0, "YXZ")

  const loader = new GLTFLoader()
  loader.load(url, (gltf) => {
    const model = gltf.scene
    model.updateWorldMatrix(true, true)
    const box = new THREE.Box3().setFromObject(model)
    const size = new THREE.Vector3()
    box.getSize(size)
    if (size.x + size.y + size.z <= 0) {
      const label = fallback?.querySelector("[data-glb-status]")
      if (label) label.textContent = "Preview unavailable"
      return
    }
    model.position.sub(box.getCenter(new THREE.Vector3()))
    group.add(model)

    fitted = size
    fitCamera(size)
    if (fallback) fallback.classList.add("hidden")
  }, undefined, () => {
    const label = fallback?.querySelector("[data-glb-status]")
    if (label) label.textContent = "Preview unavailable"
  })

  const resize = () => {
    const w = Math.max(1, canvas.clientWidth)
    const h = Math.max(1, canvas.clientHeight)
    renderer.setSize(w, h, false)
    if (fitted) {
      fitCamera(fitted)
    } else {
      camera.aspect = w / h
      camera.updateProjectionMatrix()
    }
  }

  const tick = (now) => {
    if (!running) return
    const dt = Math.min(0.05, (now - last) / 1000)
    last = now
    if (!dragging) orbit.yaw += opts.spin * dt
    applyOrbit()
    renderer.render(scene, camera)
    requestAnimationFrame(tick)
  }

  const onPointerDown = (e) => {
    dragging = true
    lastX = e.clientX
    lastY = e.clientY
    canvas.setPointerCapture(e.pointerId)
  }
  const onPointerMove = (e) => {
    if (!dragging) return
    orbit.yaw += (e.clientX - lastX) * 0.005
    orbit.pitch = Math.max(-1.2, Math.min(0.9, orbit.pitch + (e.clientY - lastY) * 0.004))
    lastX = e.clientX
    lastY = e.clientY
  }
  const onPointerUp = () => {
    dragging = false
  }

  if (opts.interactive) {
    canvas.addEventListener("pointerdown", onPointerDown)
    canvas.addEventListener("pointermove", onPointerMove)
    canvas.addEventListener("pointerup", onPointerUp)
    canvas.addEventListener("pointercancel", onPointerUp)
  }

  const ro = new ResizeObserver(resize)
  ro.observe(root)
  resize()
  applyOrbit()
  requestAnimationFrame(tick)

  return {
    dispose() {
      running = false
      ro.disconnect()
      canvas.removeEventListener("pointerdown", onPointerDown)
      canvas.removeEventListener("pointermove", onPointerMove)
      canvas.removeEventListener("pointerup", onPointerUp)
      canvas.removeEventListener("pointercancel", onPointerUp)
      scene.traverse((o) => {
        if (o.geometry) o.geometry.dispose()
        const materials = Array.isArray(o.material) ? o.material : o.material ? [o.material] : []
        for (const m of materials) {
          if (m.map) m.map.dispose()
          m.dispose()
        }
      })
      renderer.dispose()
      // Free the context now; at most 12 live previews would otherwise
      // pile up until GC.
      renderer.forceContextLoss?.()
    },
  }
}
